import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, AlertCircle, Loader2, MessageSquare, Download, ArrowLeft, Send, RefreshCw, Ticket } from 'lucide-react';
import { Button } from '../ui/button';

interface MyTicketsProps {
  clientId: string;
  token: string;
}

interface TicketSummary {
  id: string;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed' | 'on-hold';
  emailAddress: string;
  createdAt: string;
  updatedAt: string;
}

interface TicketDetail extends TicketSummary {
  queryName: string;
  companyName: string;
  phoneNumber: string;
  assignedTo?: string;
  resolutionNotes?: string;
  resolvedAt?: string;
  closedAt?: string;
  attachments: Array<{ id: string; fileName: string; fileType: string; fileSize: number; filePath: string; createdAt: string; }>;
  comments: Array<{ id: string; text: string; commentedBy: string; isInternal: boolean; createdAt: string; }>;
}

const PRIORITY_STYLES: Record<string, string> = {
  low:      'bg-blue-500/10 text-blue-500 border border-blue-500/30',
  medium:   'bg-yellow-500/10 text-yellow-500 border border-yellow-500/30',
  high:     'bg-orange-500/10 text-orange-500 border border-orange-500/30',
  critical: 'bg-red-500/10 text-red-500 border border-red-500/30',
};

const STATUS_STYLES: Record<string, string> = {
  open:          'bg-blue-500/10 text-blue-500 border border-blue-500/30',
  'in-progress': 'bg-purple-500/10 text-purple-500 border border-purple-500/30',
  resolved:      'bg-emerald-500/10 text-emerald-500 border border-emerald-500/30',
  closed:        'bg-muted text-muted-foreground border border-border',
  'on-hold':     'bg-amber-500/10 text-amber-500 border border-amber-500/30',
};

const Badge = ({ value, styleMap }: { value: string; styleMap: Record<string, string> }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${styleMap[value] || 'bg-muted text-muted-foreground'}`}>
    {value.replace('-', ' ')}
  </span>
);

const timeAgo = (dateStr: string) => {
  const s = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

export default function MyTickets({ clientId, token }: MyTicketsProps) {
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [selected, setSelected] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const url = `/api/clients/${clientId}/tickets${filter !== 'all' ? `?status=${filter}` : ''}`;
      const res = await fetch(url, { headers: authHeaders, cache: 'no-store' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch tickets');
      setTickets(data.tickets || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  }, [clientId, filter]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const fetchDetail = async (ticketId: string) => {
    try {
      setDetailLoading(true);
      const res = await fetch(`/api/clients/${clientId}/tickets/${ticketId}`, { headers: authHeaders, cache: 'no-store' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch ticket');
      setSelected({
        ...data.ticket,
        attachments: data.attachments || data.ticket?.attachments || [],
        comments: data.comments || data.ticket?.comments || [],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch ticket');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || !newComment.trim()) return;
    try {
      setAddingComment(true);
      const res = await fetch(`/api/clients/${clientId}/tickets/${selected.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        cache: 'no-store',
        body: JSON.stringify({ commentText: newComment, isInternal: false }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add comment');
      setNewComment('');
      await fetchDetail(selected.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  // ─── Ticket List ───────────────────────────────────────────
  if (!selected) {
    return (
      <div className="w-full max-w-4xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <MessageSquare className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">My Support Tickets</h1>
                <p className="text-muted-foreground text-sm">Track all your support requests</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchTickets} className="gap-2">
              <RefreshCw className="h-4 w-4" />Refresh
            </Button>
          </div>
        </motion.div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
            <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${
                filter === f.value
                  ? 'bg-purple-500/10 border-purple-500/40 text-purple-500'
                  : 'border-border text-muted-foreground hover:border-muted-foreground bg-background'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Card */}
        <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-16 text-center">
              <Loader2 className="mx-auto h-10 w-10 text-purple-500 animate-spin mb-4" />
              <p className="text-muted-foreground">Loading tickets...</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className="p-16 text-center">
              <Ticket className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Tickets Yet</h3>
              <p className="text-muted-foreground">You haven't created any support tickets yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {tickets.map((ticket, i) => (
                <motion.div
                  key={ticket.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => fetchDetail(ticket.id)}
                  className="p-5 hover:bg-accent/50 cursor-pointer transition-colors flex items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <h3 className="font-semibold text-foreground">{ticket.title}</h3>
                      <Badge value={ticket.status} styleMap={STATUS_STYLES} />
                      <Badge value={ticket.priority} styleMap={PRIORITY_STYLES} />
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-1 mb-2">{ticket.description}</p>
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      <span>{ticket.category}</span>
                      <span>Created {timeAgo(ticket.createdAt)}</span>
                      <span className="hidden sm:block font-mono">{ticket.id.slice(0, 24)}…</span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Ticket Detail ──────────────────────────────────────────
  if (detailLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-10 w-10 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Back */}
      <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-5 text-sm font-medium transition-colors group">
        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />Back to Tickets
      </button>

      <AnimatePresence mode="wait">
        <motion.div key={selected.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {/* Title Card */}
          <div className="bg-card border border-border rounded-2xl p-6 shadow-lg">
            <div className="flex items-start justify-between gap-4 mb-4">
              <h1 className="text-2xl font-bold text-foreground">{selected.title}</h1>
              <div className="flex gap-2 shrink-0">
                <Badge value={selected.status} styleMap={STATUS_STYLES} />
                <Badge value={selected.priority} styleMap={PRIORITY_STYLES} />
              </div>
            </div>
            <p className="text-xs font-mono text-muted-foreground">{selected.id}</p>
          </div>

          {/* Meta Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Ticket Details</h3>
              <dl className="space-y-2 text-sm">
                {[
                  ['Category', selected.category],
                  ['Assigned To', selected.assignedTo || 'Unassigned'],
                  ['Created', fmtDate(selected.createdAt)],
                  ...(selected.resolvedAt ? [['Resolved', fmtDate(selected.resolvedAt)]] : []),
                ].map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <dt className="text-muted-foreground w-24 shrink-0">{k}:</dt>
                    <dd className="text-foreground font-medium">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Contact Info</h3>
              <dl className="space-y-2 text-sm">
                {[
                  ['Email', selected.emailAddress],
                  ['Phone', selected.phoneNumber || 'N/A'],
                  ['Company', selected.companyName || 'N/A'],
                  ['Query', selected.queryName || 'N/A'],
                ].map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <dt className="text-muted-foreground w-24 shrink-0">{k}:</dt>
                    <dd className="text-foreground font-medium truncate">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          {/* Description */}
          <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Description</h3>
            <p className="text-foreground whitespace-pre-wrap text-sm leading-relaxed">{selected.description || 'No description provided.'}</p>
          </div>

          {/* Attachments */}
          {selected.attachments?.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Attachments ({selected.attachments.length})</h3>
              <div className="space-y-2">
                {selected.attachments.map(a => (
                  <div key={a.id} className="flex items-center justify-between bg-muted/50 border border-border rounded-lg p-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">{a.fileName}</p>
                      <p className="text-xs text-muted-foreground">{(a.fileSize / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <a href={a.filePath} download className="p-2 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                      <Download className="h-4 w-4" />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resolution Notes */}
          {selected.resolutionNotes && (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-3">Resolution Notes</h3>
              <p className="text-foreground whitespace-pre-wrap text-sm">{selected.resolutionNotes}</p>
            </div>
          )}

          {/* Comments */}
          <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Messages & Comments</h3>
            <div className="space-y-3 mb-5">
              {selected.comments?.length > 0 ? selected.comments.map(c => (
                <div key={c.id} className={`p-4 rounded-xl text-sm ${c.isInternal ? 'bg-amber-500/5 border border-amber-500/20' : 'bg-blue-500/5 border border-blue-500/15'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">{c.commentedBy}</span>
                      {c.isInternal && <span className="text-xs bg-amber-500/10 text-amber-500 px-2 py-0.5 rounded-full border border-amber-500/20">Internal</span>}
                    </div>
                    <span className="text-xs text-muted-foreground">{timeAgo(c.createdAt)}</span>
                  </div>
                  <p className="text-foreground whitespace-pre-wrap">{c.text}</p>
                </div>
              )) : (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="mx-auto h-8 w-8 mb-2 opacity-30" />
                  <p className="text-sm">No comments yet</p>
                </div>
              )}
            </div>

            {/* Add Comment */}
            <form onSubmit={handleAddComment} className="flex gap-3 items-end border-t border-border pt-4">
              <div className="flex-1">
                <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Add a Message</label>
                <textarea
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder="Type your message here..."
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all text-sm resize-none"
                />
              </div>
              <Button type="submit" disabled={addingComment || !newComment.trim()} className="gap-2 shrink-0 mb-0">
                {addingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {addingComment ? '' : 'Send'}
              </Button>
            </form>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
